import { faUserClock } from '@fortawesome/free-solid-svg-icons';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  EmptyState,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  Table,
  TableWrap,
  TBody,
  TD,
  TDActions,
  TH,
  THead,
  TR,
} from '../../components/ui';
import { CheckRow } from '../ServiceProviderPage/serviceControls';
import {
  useAdminWorkerStrikesQuery,
  useResolveWorkerStrikeMutation,
  useResumeWorkerMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { strikePayload } from '../ServiceJobPage/jobTimeline';
import { stayErrorMessage } from '../StayHostPage/stayErrors';

const TYPE_LABELS: Record<string, string> = {
  LATE_CANCEL: 'Late cancellation',
  NO_SHOW: 'No-show',
};

/**
 * Worker strikes (spec 019 W23, D17): late cancellations and no-shows of the
 * last 90 days on this brand, with the Provider's appeal. Remove or uphold a
 * strike; resume a worker the strikes paused. Brand admins only.
 */
export default function AdminServiceWorkersPage() {
  const [pendingOnly, setPendingOnly] = useState(true);
  const query = useAdminWorkerStrikesQuery({
    variables: { pendingAppealOnly: pendingOnly },
    fetchPolicy: 'network-only',
  });
  const [resolve] = useResolveWorkerStrikeMutation({ refetchQueries: ['AdminWorkerStrikes'] });
  const [resume] = useResumeWorkerMutation();
  const [busy, setBusy] = useState<string | null>(null);
  const strikes = query.data?.adminWorkerStrikes ?? [];

  const act = async (key: string, run: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await run();
      toast.success(done);
    } catch (e) {
      toast.error(stayErrorMessage(e, 'Not saved'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageContainer width="xl" canvas data-testid="admin-service-workers">
      <PageHeader
        eyebrow="Admin"
        title="Worker strikes"
        description="A worker is paused automatically after too many strikes. Removing a strike does not resume the worker; use Resume."
      />
      <Stack $gap={6}>
        <CheckRow
          label="Only strikes with an appeal to review"
          checked={pendingOnly}
          onChange={setPendingOnly}
        />
        <Card>
          <CardHeader title={pendingOnly ? 'Appeals to review' : 'All strikes, last 90 days'} />
          {query.loading && !query.data ? (
            <LoadingBlock label="Loading" />
          ) : query.error && !query.data ? (
            <Alert tone="error" title="Could not load">
              {stayErrorMessage(query.error)}
            </Alert>
          ) : strikes.length === 0 ? (
            <EmptyState icon={faUserClock} compact title="Nothing to review" />
          ) : (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>Strike</TH>
                    <TH>Job and worker</TH>
                    <TH>Appeal</TH>
                    <TH $align="right">Decision</TH>
                  </TR>
                </THead>
                <TBody>
                  {strikes.map(strike => {
                    const p = strikePayload(strike.payload);
                    return (
                      <TR key={strike.id} data-testid={`strike-${strike.id}`}>
                        <TD>
                          <div>{TYPE_LABELS[strike.type] ?? strike.type}</div>
                          <Muted $size="xs">
                            {DateTime.fromISO(strike.receivedAt).toFormat('LLL d, h:mm a')}
                          </Muted>
                        </TD>
                        <TD>
                          <Muted $size="xs">Job {p.bookingId?.slice(0, 8) ?? '?'}</Muted>
                          <div>
                            <Muted $size="xs">Worker {p.workerCarId?.slice(0, 8) ?? '?'}</Muted>
                          </div>
                        </TD>
                        <TD>
                          {p.appealNote ? (
                            <div>{p.appealNote}</div>
                          ) : (
                            <Muted $size="sm">No appeal</Muted>
                          )}
                          {p.appealFileKey && (
                            <code style={{ fontSize: 11, wordBreak: 'break-all' }}>
                              {p.appealFileKey}
                            </code>
                          )}
                        </TD>
                        <TDActions>
                          {strike.voidedAt || p.decided ? (
                            <Badge
                              tone={strike.voidedAt || p.upheld === false ? 'success' : 'neutral'}
                            >
                              {strike.voidedAt || p.upheld === false ? 'Removed' : 'Upheld'}
                            </Badge>
                          ) : (
                            <InlineRow>
                              <Button
                                type="button"
                                size="sm"
                                text="Remove"
                                isLoading={busy === `${strike.id}:false`}
                                onClick={() =>
                                  act(
                                    `${strike.id}:false`,
                                    () =>
                                      resolve({ variables: { eventId: strike.id, upheld: false } }),
                                    'Strike removed',
                                  )
                                }
                                dataTestId={`strike-remove-${strike.id}`}
                              />
                              <Button
                                type="button"
                                theme="secondary"
                                size="sm"
                                text="Uphold"
                                isLoading={busy === `${strike.id}:true`}
                                onClick={() =>
                                  act(
                                    `${strike.id}:true`,
                                    () =>
                                      resolve({ variables: { eventId: strike.id, upheld: true } }),
                                    'Strike upheld',
                                  )
                                }
                              />
                            </InlineRow>
                          )}
                          {p.workerCarId && (
                            <Button
                              type="button"
                              theme="text"
                              size="sm"
                              text="Resume worker"
                              isLoading={busy === `resume:${p.workerCarId}`}
                              onClick={() =>
                                act(
                                  `resume:${p.workerCarId}`,
                                  () => resume({ variables: { workerCarId: p.workerCarId! } }),
                                  'Worker can take jobs again',
                                )
                              }
                            />
                          )}
                        </TDActions>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </Stack>
    </PageContainer>
  );
}
